/**
 * Lane C0-TRUEPX-GAME: the true-pixel clean-up of a finished picture, on packed RGBA pixels (ImageData order,
 * 0xAABBGGRR), in place. The game's port of tools/truepx/ramps.ts `cleanFrame` (lane C0-TRUEPX), with the shared
 * palette (truepx/palette.ts) in place of a per-figure one, so it costs a lookup per colour instead of a k-means.
 *
 * Sprites (figures, scenery, buildings), `cleanSprite`:
 *  1. snap every opaque pixel to the shared palette;
 *  2. small clusters: a patch of up to 3 pixels inside one shape, at most two steps of the same ramp off the tone
 *     around it (stray specks, broken stripe ends), takes the surrounding tone;
 *  3. cel light from the upper right: the lit edge of each shape (open or outlined to its right or above) one ramp
 *     step up;
 *  4. selective outline: each dark outline pixel becomes the line tone of the colour inside it, one step lighter on
 *     the lit side. Outlines are only recoloured, never added: a soft-edged sprite keeps its edge.
 * The ground, `cleanGround`: snap, and lone specks (patches of up to 2 pixels in a field of a neighbouring step of
 * the same ramp: sparse dither) merged into their field. A 50% checker stays (its diagonals join it up).
 *
 * Written for speed (it runs as art is made, on whichever thread makes it): typed arrays, inline neighbours, and a
 * pixel with as many same-tone neighbours as a small patch could have is skipped before any search.
 */
import { nearestTone, palette } from './palette';

const DX = [-1, 0, 1, -1, 1, -1, 0, 1] as const;
const DY = [-1, -1, -1, 0, 0, 1, 1, 1] as const;

/** Every opaque pixel's palette tone (-1 for clear ones). */
function snapAll(px: Uint32Array, id: Int16Array): void {
  let last = -1;
  let lastT = 0;
  for (let i = 0; i < px.length; i++) {
    const p = px[i]!;
    if (p >>> 24 < 128) continue;
    if (p !== last) {
      last = p;
      lastT = nearestTone(p);
    }
    id[i] = lastT;
  }
}

/**
 * Patches of up to `max` pixels of one tone (8-connected, among pixels with `ok` set) take the commonest tone around
 * them when it is at most two steps of the same ramp away. A patch touching a pixel without `ok` is kept.
 */
function mergeSmall(
  id: Int16Array,
  ok: Uint8Array,
  w: number,
  h: number,
  max: number,
  mark: Int32Array,
  yFrom = 0,
  yTo = h,
): void {
  const P = palette();
  const comp = new Int32Array(max);
  const qt = new Int16Array(8 * max);
  const qc = new Int16Array(8 * max);
  for (let y0 = yFrom; y0 < yTo; y0++)
    for (let x0 = 0; x0 < w; x0++) {
      const s0 = y0 * w + x0;
      const t = id[s0]!;
      if (t < 0 || !ok[s0] || mark[s0]! < 0) continue;
      // Quick test: as many same-tone neighbours as a patch of `max` could hold means a bigger patch.
      let same = 0;
      for (let d = 0; d < 8 && same < max; d++) {
        const X = x0 + DX[d]!;
        const Y = y0 + DY[d]!;
        if (X < 0 || Y < 0 || X >= w || Y >= h) continue;
        const j = Y * w + X;
        if (id[j] === t && ok[j]) same++;
      }
      if (same >= max) continue;
      const stamp = s0 + 1;
      let len = 1;
      comp[0] = s0;
      mark[s0] = stamp;
      let big = false;
      for (let k = 0; k < len && !big; k++) {
        const i = comp[k]!;
        const x = i % w;
        const y = (i - x) / w;
        for (let d = 0; d < 8; d++) {
          const X = x + DX[d]!;
          const Y = y + DY[d]!;
          if (X < 0 || Y < 0 || X >= w || Y >= h) continue;
          const j = Y * w + X;
          if (id[j] !== t || !ok[j] || mark[j] === stamp) continue;
          if (len === max) {
            big = true;
            break;
          }
          mark[j] = stamp;
          comp[len++] = j;
        }
      }
      if (big) continue;
      // The tones round the patch.
      let m = 0;
      let blocked = false;
      for (let k = 0; k < len && !blocked; k++) {
        const i = comp[k]!;
        const x = i % w;
        const y = (i - x) / w;
        for (let d = 0; d < 8; d++) {
          const X = x + DX[d]!;
          const Y = y + DY[d]!;
          if (X < 0 || Y < 0 || X >= w || Y >= h) continue;
          const j = Y * w + X;
          const q = id[j]!;
          if (q === t) {
            if (mark[j] === stamp) continue;
            blocked = true;
            break;
          }
          if (q < 0 || !ok[j]) {
            blocked = true;
            break;
          }
          let e = 0;
          while (e < m && qt[e] !== q) e++;
          if (e === m) {
            qt[m] = q;
            qc[m++] = 0;
          }
          qc[e]!++;
        }
      }
      for (let k = 0; k < len; k++) mark[comp[k]!] = -1;
      if (blocked || !m) continue;
      let top = 0;
      for (let e = 1; e < m; e++) if (qc[e]! > qc[top]!) top = e;
      const q = qt[top]!;
      if (P.ramp[q] !== P.ramp[t] || Math.abs(P.step[q]! - P.step[t]!) > 2) continue;
      for (let k = 0; k < len; k++) id[comp[k]!] = q;
    }
}

/**
 * A sprite's clean-up, in place. `rim`: how many canvas pixels deep its outline is (a rig figure's is one per canvas
 * pixel of an art pixel); `light`: add the cel light on the upper-right edges.
 */
export function cleanSprite(px: Uint32Array, w: number, h: number, rim = 1, light = true): void {
  const P = palette();
  const n = w * h;
  const id = new Int16Array(n).fill(-1);
  snapAll(px, id);
  // Outline pixels (`line`): dark ones within `rim` of the outside; `sun`: open above or to the right (the lit side).
  // `ok`: the inside of the shapes (opaque, not outline).
  const line = new Uint8Array(n);
  const sun = new Uint8Array(n);
  const ok = new Uint8Array(n);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (id[i]! < 0) continue;
      let open = false;
      let lit = false;
      for (let k = 1; k <= rim && !lit; k++) {
        if (y - k < 0 || id[i - k * w]! < 0 || x + k >= w || id[i + k]! < 0) lit = open = true;
        else if (y + k >= h || id[i + k * w]! < 0 || x - k < 0 || id[i - k]! < 0) open = true;
      }
      if (lit) sun[i] = 1;
      const p = px[i]!;
      if (open && 0.299 * (p & 255) + 0.587 * ((p >>> 8) & 255) + 0.114 * ((p >>> 16) & 255) < 72)
        line[i] = 1;
      else ok[i] = 1;
    }
  mergeSmall(id, ok, w, h, 3, new Int32Array(n));
  if (light) {
    // The lit edge: inside pixels whose right or upper neighbour is outside or outline.
    const lit = id.slice();
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (!ok[i]) continue;
        const u = P.up[id[i]!]!;
        if (u < 0) continue;
        if (x + 1 >= w || !ok[i + 1] || y === 0 || !ok[i - w]) lit[i] = u;
      }
    id.set(lit);
  }
  const tone = P.tone;
  for (let i = 0; i < n; i++) {
    const t = id[i]!;
    if (t < 0) continue;
    const a = px[i]! & 0xff000000;
    if (!line[i]) {
      px[i] = ((tone[t]! & 0xffffff) | a) >>> 0;
      continue;
    }
    // The colour the outline wraps: the commonest inside tone round it, out to the outline's depth.
    const x = i % w;
    const y = (i - x) / w;
    let m = -1;
    for (let r = 1; r <= rim && m < 0; r++) {
      let best = 0;
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          if (dx !== -r && dx !== r && dy !== -r && dy !== r) continue;
          const X = x + dx;
          const Y = y + dy;
          if (X < 0 || Y < 0 || X >= w || Y >= h) continue;
          const j = Y * w + X;
          if (!ok[j]) continue;
          // Votes counted by a second pass over the same ring (rings are at most 16 pixels).
          let c = 0;
          const q = id[j]!;
          for (let ey = -r; ey <= r; ey++)
            for (let ex = -r; ex <= r; ex++) {
              if (ex !== -r && ex !== r && ey !== -r && ey !== r) continue;
              const XX = x + ex;
              const YY = y + ey;
              if (XX < 0 || YY < 0 || XX >= w || YY >= h) continue;
              const jj = YY * w + XX;
              if (ok[jj] && id[jj] === q) c++;
            }
          if (c > best) {
            best = c;
            m = q;
          }
        }
    }
    if (m < 0) {
      px[i] = ((tone[t]! & 0xffffff) | a) >>> 0;
      continue;
    }
    const d = P.down[m]! >= 0 ? P.down[m]! : m;
    const lt = sun[i] ? P.line[d]! : P.line[P.down[d]! >= 0 ? P.down[d]! : d]!;
    px[i] = ((lt & 0xffffff) | a) >>> 0;
  }
}

/**
 * The ground's clean-up, in place (snapped, lone specks merged into their field), done a band of rows at a time so a
 * chunk painted on the main thread keeps to the ground's time budget (terrain.ts `paintSome`). A patch is only ever
 * changed from its first row on, so rows above the band merged so far are final and written out at once.
 */
export class GroundClean {
  private readonly id: Int16Array;
  private readonly ok: Uint8Array;
  private readonly mark: Int32Array;
  /** Rows snapped, and rows merged (and written) so far. */
  private snapped = 0;
  private merged = 0;

  constructor(
    private readonly px: Uint32Array,
    private readonly w: number,
    private readonly h: number,
  ) {
    const n = w * h;
    this.id = new Int16Array(n).fill(-1);
    this.ok = new Uint8Array(n);
    this.mark = new Int32Array(n);
  }

  /** Works on until `until` (performance.now()); true once the whole picture is done. */
  step(until: number): boolean {
    const { px, w, h, id, ok } = this;
    const tone = palette().tone;
    const BAND = 16;
    // A patch reaches at most one row below its first, and its surroundings one more.
    const AHEAD = 3;
    while (this.merged < h) {
      const y1 = Math.min(h, this.merged + BAND);
      const s1 = Math.min(h, y1 + AHEAD);
      if (s1 > this.snapped) {
        const a = this.snapped * w;
        const b = s1 * w;
        snapAll(px.subarray(a, b), id.subarray(a, b));
        for (let i = a; i < b; i++) if (id[i]! >= 0) ok[i] = 1;
        this.snapped = s1;
      }
      mergeSmall(id, ok, w, h, 2, this.mark, this.merged, y1);
      for (let i = this.merged * w, e = y1 * w; i < e; i++) {
        const t = id[i]!;
        if (t >= 0) px[i] = ((tone[t]! & 0xffffff) | (px[i]! & 0xff000000)) >>> 0;
      }
      this.merged = y1;
      if (performance.now() >= until) break;
    }
    return this.merged >= h;
  }
}

/** The ground's clean-up, all at once. */
export function cleanGround(px: Uint32Array, w: number, h: number): void {
  new GroundClean(px, w, h).step(Infinity);
}
